import Alert, { AlertColor } from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Fade from '@mui/material/Fade'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import i18next from 'i18next'
import { ChangeEvent, useCallback, useState } from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { Constants } from '../../data/constants'
import { SupportCase } from '../../data/support'

const codeTotalRequiredLength = 6
const commentCharacterLimit = 250

export default function ContactForm() {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const [sendDisabled, setSendDisabled] = useState(true)
  const topics: string[] = i18next.t('contact-support.topics', { returnObjects: true })
  const helpers: string[] = i18next.t('contact-support.helpers', { returnObjects: true })
  const subtopics: string[] = i18next.t('contact-support.subtopics', { returnObjects: true })
  const [recaptchaToken, setRecaptchaToken] = useState('')
  const [selectedTopic, setSelectedTopic] = useState('')
  const [selectedSubtopic, setSelectedSubtopic] = useState('')
  const [helperText, setHelperText] = useState('')
  const [NFCode, setNFCode] = useState('')
  const [details, setDetails] = useState('')
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor | null>()
  const snackbarPosition: SnackbarOrigin = {
    vertical: 'bottom',
    horizontal: 'right'
  }

  const verifyReCaptchaCallback = useCallback((token: string) => {
    setRecaptchaToken(token)
  }, [])

  const setSnackbar = (severity: string) => {
    setAlertOpen(true)

    if (severity === 'success') {
      setSnackbarSeverity('success')
      setSnackbarMessage(t('contact-support.snackbar.sent-success'))
    } else if (severity === 'error') {
      setSnackbarSeverity('error')
      setSnackbarMessage(t('contact-support.snackbar.sent-error'))
    }
  }

  const sendCase = async () => {
    setSendDisabled(true)

    if (recaptchaToken) {
      const url = `api/support/case`

      const supportCase: SupportCase = {
        Topic: selectedTopic,
        NFCode: NFCode ? `${Constants.CODE_PREFIX}${NFCode}` : undefined,
        Subject: selectedSubtopic ?? undefined,
        Description: details
      }

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ReCaptcha-Token': recaptchaToken
        },
        body: JSON.stringify(supportCase)
      })
        .then(response => {
          resetCase(false)

          if (!response.ok) {
            throw new Error('Unable to send message to support')
          }

          setSnackbar('success')
        })
        .catch(_error => {
          setSnackbar('error')
        })
    }
  }

  const selectTopic = (event: any) => {
    const { key, value } = event.target.dataset

    resetCase(true)
    setSelectedTopic(value)
    setHelperText(helpers[key])
  }

  const selectSubtopic = (event: any) => {
    setSelectedSubtopic(event.target.dataset.value)
  }

  const handleChangeNFCode = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value

    if (inputValue.length > codeTotalRequiredLength) {
      return
    }

    setNFCode(inputValue)
  }

  const handleChangeDetails = (event: ChangeEvent<HTMLInputElement>) => {
    const comment = event.target.value

    if (comment.length >= commentCharacterLimit) {
      return
    }

    setDetails(comment)

    if (details && sendDisabled) {
      setSendDisabled(false)
    }
  }

  const resetCase = (dialogOpen: boolean) => {
    setSelectedTopic('')
    setSelectedSubtopic('')
    setHelperText('')
    setDetails('')
    setNFCode('')
    setSnackbarMessage('')
    setSnackbarSeverity(null)
    setDialogOpen(dialogOpen)
    setSendDisabled(true)
  }

  const handleCloseAlert = (_event?: React.SyntheticEvent | Event, _reason?: string) => {
    setSnackbarMessage('')
    setSnackbarSeverity(null)
    setAlertOpen(false)
  }

  return (
    <>
      <Link onClick={() => setDialogOpen(true)}>{t('contact-support.button')}</Link>
      {topics.length > 0 && helpers.length > 0 && (
        <Dialog maxWidth="xs" open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle textAlign={'center'} marginLeft={0}>
            {t('contact-support.title')}
          </DialogTitle>
          <DialogContent>
            {typeof selectedTopic !== 'undefined' && (
              <>
                <FormControl fullWidth>
                  <InputLabel id="contact-form-select-label">{t('contact-support.placeholder')}</InputLabel>
                  <Select
                    fullWidth
                    id="contact-form-select"
                    labelId="contact-form-select-label"
                    label={t('contact-support.placeholder')}
                    variant="outlined"
                    value={selectedTopic}
                  >
                    {topics.map((topic: string, index: number) => (
                      <MenuItem dense key={index + 1} data-key={index} value={topic} onClick={selectTopic}>
                        {topic}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedTopic === topics[0] && (
                  <TextField
                    value={NFCode}
                    type="number"
                    InputProps={{ startAdornment: Constants.CODE_PREFIX }}
                    onChange={handleChangeNFCode}
                    fullWidth
                  ></TextField>
                )}
                {selectedTopic === topics[3] && (
                  <FormControl fullWidth>
                    <InputLabel id="contact-form-select-subtopic-label">{t('contact-support.subtopic')}</InputLabel>
                    <Select
                      fullWidth
                      id="contact-form-select-subtopic"
                      labelId="contact-form-select-subtopic-label"
                      label={t('contact-support.subtopic')}
                      variant="outlined"
                      required
                      value={selectedSubtopic}
                    >
                      {subtopics.map((subtopic: string, index: number) => (
                        <MenuItem dense key={index} data-key={index} value={subtopic} onClick={selectSubtopic}>
                          {subtopic}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {helperText && (
                  <FormControl fullWidth>
                    <Typography variant="subtitle1" textAlign="center">
                      {helperText}
                    </Typography>
                    <TextField
                      value={details}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeDetails(event)}
                      placeholder={t('contact-support.comments')}
                      multiline
                      required
                      rows={5}
                    ></TextField>
                  </FormControl>
                )}
                <Typography variant="subtitle2">{t('contact-support.required')}</Typography>
                <Typography marginTop={3} fontSize=".65em" variant="subtitle2" textAlign={'center'}>
                  {t('contact-support.recaptcha.0')}{' '}
                  <Link
                    color="#B7CBD3"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://policies.google.com/privacy"
                  >
                    {t('contact-support.recaptcha.1')}
                  </Link>
                  {t('contact-support.recaptcha.2')}{' '}
                  <Link
                    color="#B7CBD3"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://policies.google.com/terms"
                  >
                    {t('contact-support.recaptcha.3')}
                  </Link>
                  {t('contact-support.recaptcha.4')}
                </Typography>
              </>
            )}
            <GoogleReCaptcha onVerify={verifyReCaptchaCallback} />
          </DialogContent>
          <DialogActions className="dialog-action-container">
            <Button
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
              color="primary"
              size="large"
              onClick={() => resetCase(false)}
            >
              {t('contact-support.cancel')}
            </Button>
            <Button
              variant="contained"
              sx={{ textTransform: 'capitalize' }}
              disabled={sendDisabled}
              color="secondary"
              size="large"
              onClick={sendCase}
            >
              {t('contact-support.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {snackbarSeverity && snackbarMessage && (
        <Snackbar
          open={alertOpen}
          onClose={handleCloseAlert}
          autoHideDuration={5000}
          anchorOrigin={snackbarPosition}
          TransitionComponent={Fade}
        >
          <Alert variant="filled" severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}
